import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { BillingInformation } from '@/interfaces/billingInformations'
import { PolicyInformation } from '@/interfaces/policyInformation'

import {
	HotelPnrRequest,
	PassengerGroup,
} from '@/modules/hotels/interfaces/hotelPnrRequest'
import { HotelRate } from '@/modules/hotels/interfaces/hotelRate'
import { RateForm } from '@/modules/hotels/interfaces/rateForm'

import { useHotelRatePayloadStore } from './hotelRatePayload'

export const useHotelRateStore = defineStore('hotelRate', () => {
	const hotelRatePayload = useHotelRatePayloadStore()

	const hotelRate = ref<HotelRate | undefined>(undefined)
	const form = ref<Partial<RateForm>>()
	const payload = ref<Partial<HotelPnrRequest>>()
	const billingInformation = ref<BillingInformation>()
	const requireInvoice = ref<boolean>(false)
	const companyBilling = ref<number>()
	const mandatoryCostCenter = ref<boolean>(false)
	const policyInformation = ref<PolicyInformation>()

	return {
		hotelRate,
		form,
		payload,
		billingInformation,
		requireInvoice,
		companyBilling,
		mandatoryCostCenter,
		policyInformation,

		policyErrorFlag: computed(() => {
			const { errors, warnings } = policyInformation.value || {}
			return !!(errors?.length || warnings?.length || false)
		}),

		getPassengerReference() {
			const roomData = Array<PassengerGroup>()

			if (form.value?.travelers) {
				form.value.travelers.map((room) => {
					roomData.push([
						{
							name: room.mainGuest.name,
							surname: room.mainGuest.lastname,
							email: room.email,
							type: 'ADT',
							principal: true,
						},
						...(room.additionalGuests?.map((instance) => {
							return {
								name: instance.name,
								surname: instance.lastname,
								type: 'ADT',
								principal: false,
							}
						}) || []),
					])
				})
			}

			return roomData
		},

		setHotelRate(newHotelRate: HotelRate | undefined) {
			hotelRate.value = newHotelRate
		},
		setPayloadValue<T extends keyof HotelPnrRequest>(
			property: T,
			value: HotelPnrRequest[T]
		) {
			payload.value![property] = value
			localStorage.setItem('hotelRatePayload', JSON.stringify(payload.value))
		},
		setBillingInformation(newBillingInformation: BillingInformation) {
			billingInformation.value = newBillingInformation
		},
		setRequireInvoice(newRequireInvoice: boolean) {
			requireInvoice.value = newRequireInvoice
		},
		setCompanyBilling(newCompanyBilling: number) {
			companyBilling.value = newCompanyBilling
		},
		setMandatoryCostCenter(newValue: boolean) {
			mandatoryCostCenter.value = newValue
		},
		setPolicyInformation(value: PolicyInformation) {
			policyInformation.value = value
		},
		resetBillingData() {
			billingInformation.value = undefined
			requireInvoice.value = false
			companyBilling.value = undefined
		},
		fillFormValues(newValues: Partial<RateForm>) {
			form.value = { ...form.value, ...newValues }
		},
		fillPayloadValues(newValues: Partial<HotelPnrRequest>) {
			payload.value = { ...payload.value, ...newValues }
		},
	}
})
